import React, { useEffect, useState } from "react";
import {
  Breadcrumb,
  Divider,
  Card,
  Form,
  Input,
  Switch,
  Button,
  Row,
  Col,
} from "antd";
import * as Icon from "react-feather";
import { useDispatch } from "react-redux";
import appLoaderAction from "../../redux/action/app-loader-action";
import notifyUtitls from "../../utils/notify-utils";
import featureConfigService from "../../services/feature-config-service";
import { FEATURE_CONFIG_KEYS } from "../../constants/app-constants";

// Field descriptors: which keys are booleans (Switch) vs strings (Input).
const BOOLEAN_KEYS = [
  FEATURE_CONFIG_KEYS.GOOGLE_LOGIN_ENABLED,
  FEATURE_CONFIG_KEYS.USERNAME_LOGIN_ENABLED,
  FEATURE_CONFIG_KEYS.STAFF_INTRANET_ENABLED,
  FEATURE_CONFIG_KEYS.EVENT_CALENDAR_ENABLED,
  FEATURE_CONFIG_KEYS.CHAT_MESSENGER_ENABLED,
];

const STRING_KEYS = [
  FEATURE_CONFIG_KEYS.GOOGLE_ALLOWED_HD,
  FEATURE_CONFIG_KEYS.CHAT_MESSENGER_URL,
];

/**
 * NMU-512: the Messenger link is rendered as an `href` on the public site, so a
 * `javascript:` value here would be a stored XSS vector and any other host would
 * be an open redirect wearing the university's chat button. nmu-web validates
 * again before rendering — this check is here so the admin finds out at save
 * time instead of wondering why the button never appears.
 */
const MESSENGER_HOSTS = [
  "m.me",
  "facebook.com",
  "www.facebook.com",
  "messenger.com",
  "www.messenger.com",
];

const validateMessengerUrl = (_rule, value) => {
  if (!value) return Promise.resolve(); // empty = feature simply stays off
  let url;
  try {
    url = new URL(value);
  } catch (e) {
    return Promise.reject(
      new Error("รูปแบบ URL ไม่ถูกต้อง ต้องขึ้นต้นด้วย https://")
    );
  }
  if (url.protocol !== "https:") {
    return Promise.reject(new Error("ต้องเป็น https:// เท่านั้น"));
  }
  if (!MESSENGER_HOSTS.includes(url.hostname.toLowerCase())) {
    return Promise.reject(
      new Error(`โดเมนต้องเป็นหนึ่งใน: ${MESSENGER_HOSTS.join(", ")}`)
    );
  }
  return Promise.resolve();
};

/** Extra per-key validation for STRING_KEYS; most keys need none. */
const FIELD_RULES = {
  [FEATURE_CONFIG_KEYS.CHAT_MESSENGER_URL]: [{ validator: validateMessengerUrl }],
};

const FIELD_LABELS = {
  [FEATURE_CONFIG_KEYS.GOOGLE_LOGIN_ENABLED]:
    "เปิดใช้งาน Google Login (google_login_enabled)",
  [FEATURE_CONFIG_KEYS.USERNAME_LOGIN_ENABLED]:
    "เปิดใช้งาน Username Login (username_login_enabled)",
  [FEATURE_CONFIG_KEYS.STAFF_INTRANET_ENABLED]:
    "เปิดใช้งานอินทราเน็ตบุคลากร (staff_intranet_enabled)",
  [FEATURE_CONFIG_KEYS.EVENT_CALENDAR_ENABLED]:
    "เปิดใช้งานปฏิทินกิจกรรม (event_calendar_enabled)",
  [FEATURE_CONFIG_KEYS.GOOGLE_ALLOWED_HD]:
    "โดเมนที่อนุญาต (google_allowed_hd)",
  [FEATURE_CONFIG_KEYS.CHAT_MESSENGER_ENABLED]:
    "เปิดตัวเลือก Facebook Messenger ในปุ่มแชท (chat_messenger_enabled)",
  [FEATURE_CONFIG_KEYS.CHAT_MESSENGER_URL]:
    "ลิงก์ Facebook Messenger เช่น https://m.me/<page> (chat_messenger_url)",
};

const toBool = (value) => value === true || value === "true";

const FeatureConfig = () => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    doLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const doLoad = async () => {
    try {
      dispatch(appLoaderAction.enable());
      const list = await featureConfigService.list();

      const map = {};
      list.forEach((item) => {
        map[item.key] = item.value;
      });

      const values = {};
      BOOLEAN_KEYS.forEach((key) => {
        values[key] = toBool(map[key]);
      });
      STRING_KEYS.forEach((key) => {
        values[key] = map[key] != null ? map[key] : "";
      });

      // default google_allowed_hd per contract
      if (!values[FEATURE_CONFIG_KEYS.GOOGLE_ALLOWED_HD]) {
        values[FEATURE_CONFIG_KEYS.GOOGLE_ALLOWED_HD] = "nmu.ac.th";
      }

      form.setFieldsValue(values);
      dispatch(appLoaderAction.disale());
    } catch (err) {
      dispatch(appLoaderAction.disale());
      notifyUtitls.error(err);
    }
  };

  const onSave = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();

      const configs = [];
      BOOLEAN_KEYS.forEach((key) => {
        configs.push({ key, value: !!values[key] });
      });
      STRING_KEYS.forEach((key) => {
        configs.push({ key, value: values[key] });
      });

      try {
        dispatch(appLoaderAction.enable());
        await featureConfigService.update(configs);
        dispatch(appLoaderAction.disale());
        notifyUtitls.success("สำเร็จ", "บันทึกสำเร็จ");
        doLoad();
      } catch (err) {
        dispatch(appLoaderAction.disale());
        notifyUtitls.error(err);
      }
    } catch (errorInfo) {
      notifyUtitls.warning("แจ้งเตือน", "โปรดระบุข้อมูลให้ครบถ้วน");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h2 className="color-primary-title">ตั้งค่าฟีเจอร์</h2>
      <Breadcrumb>
        <Breadcrumb.Item>ผู้ดูแลระบบ</Breadcrumb.Item>
        <Breadcrumb.Item>ตั้งค่าฟีเจอร์</Breadcrumb.Item>
      </Breadcrumb>
      <Divider className="mt-2 mb-4" />

      <Card bordered={false}>
        <Form
          layout="vertical"
          name="feature_config"
          form={form}
        >
          <Row gutter={16}>
            {BOOLEAN_KEYS.map((key) => (
              <Col xs={24} md={12} key={key}>
                <Form.Item
                  label={FIELD_LABELS[key]}
                  name={key}
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
              </Col>
            ))}

            {STRING_KEYS.map((key) => (
              <Col xs={24} md={12} key={key}>
                <Form.Item
                  label={FIELD_LABELS[key]}
                  name={key}
                  rules={FIELD_RULES[key]}
                >
                  <Input />
                </Form.Item>
              </Col>
            ))}
          </Row>

          <div>
            <Button type="primary" onClick={onSave} loading={loading}>
              <Icon.Save className="me-2" size={17} />
              บันทึก
            </Button>
          </div>
        </Form>
      </Card>
    </div>
  );
};

export default FeatureConfig;
